import React from 'react';
import { getGenderTextColor } from '../../utils/genderColors';
import {
  getRankTierBadgeClass,
  getRankTierTableRowClass,
} from '../../utils/leaderboardRankTiers';
import { SessionRecentFormIndicators } from './SessionRecentFormIndicators';

function RankCell({ rank, mode = 'full' }) {
  const badgeClass = getRankTierBadgeClass(rank, mode);

  if (badgeClass) {
    return (
      <span
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold ${badgeClass}`}
        aria-label={`Rank ${rank}`}
      >
        {rank}
      </span>
    );
  }

  return (
    <span className="text-sm font-medium text-text-primary">#{rank}</span>
  );
}

/**
 * Session win-% rankings table with podium / top-10 / top-25 tier styling.
 */
export default function SessionRankingsTable({
  rows = [],
  highlightPlayerId = null,
  isSelfView = false,
  showDeltaColumns = false,
  rankTierMode = 'full',
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border-medium text-sm">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
              Rank
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
              Player
            </th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
              Games
            </th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
              Win %
            </th>
            <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-text-muted">
              Form
            </th>
            {showDeltaColumns && (
              <>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Mixed Δ
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Levels Δ
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-medium">
          {rows.map((row) => {
            const isHighlighted = row.player_id === highlightPlayerId;
            const rowClass = getRankTierTableRowClass(row.rank, rankTierMode, {
              isUserHighlight: isHighlighted,
            });

            return (
              <tr key={row.player_id} className={rowClass}>
                <td className="px-3 py-2">
                  <RankCell rank={row.rank} mode={rankTierMode} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${getGenderTextColor(row.gender)}`}>
                      {row.name}
                    </span>
                    {isHighlighted && (
                      <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-text-inverse shadow-sm">
                        {isSelfView ? 'You' : 'Viewing'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-sm text-text-secondary">
                  {row.games}
                </td>
                <td className="px-3 py-2 text-right text-sm text-text-secondary">
                  {row.winPct.toFixed(0)}%
                </td>
                <td className="px-3 py-2 text-center">
                  <SessionRecentFormIndicators form={row.recentForm} />
                </td>
                {showDeltaColumns && (
                  <>
                    <td className="px-3 py-2 text-right text-sm">
                      {isHighlighted ? (
                        <span className={row.mixedDelta >= 0 ? 'text-success' : 'text-danger'}>
                          {row.mixedDelta >= 0 ? `+${row.mixedDelta}` : row.mixedDelta}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-sm">
                      {isHighlighted ? (
                        <span className={row.levelsDelta >= 0 ? 'text-success' : 'text-danger'}>
                          {row.levelsDelta >= 0 ? `+${row.levelsDelta}` : row.levelsDelta}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
